import { createContext, forwardRef, useContext, useEffect, useRef, useState } from 'react';
import 'react-phone-number-input/style.css';
import RPNInput from 'react-phone-number-input';
import { getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import * as flagModule from 'country-flag-icons/react/3x2'; // bundled flag SVGs — avoids the external CDN default (CSP-safe)

/**
 * The ES-module namespace carries a `default` export (an object) alongside the country components, and
 * react-phone-number-input prop-types the `flags` map as objectOf(func) — so passing the namespace
 * straight through logged "Invalid prop `flags.default` of type `object`" on every screen with a phone
 * field. Keep only real ISO-3166 alpha-2 keys pointing at components.
 */
const flags = Object.fromEntries(
  Object.entries(flagModule as Record<string, unknown>)
    .filter(([code, C]) => /^[A-Z]{2}$/.test(code) && typeof C === 'function'),
) as Record<string, React.ComponentType<any>>;
import { ChevronDown, Search, Globe } from 'lucide-react';
import { toE164 } from '../lib/validators';

interface Props {
  /** Stored E.164 value (e.g. +919812345678). Form.Item injects this. */
  value?: string;
  /** Emits the E.164 string (or undefined when cleared). Form.Item injects this. */
  onChange?: (v?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Accessible name for the number field. Defaults to the placeholder, which disappears as soon as
   *  the person types and is not announced as a name by every screen reader. */
  'aria-label'?: string;
}

/**
 * THE LONGEST NUMBER A PERSON CAN TYPE, PER COUNTRY.
 *
 * The library's own `limitMaxLength` stops at the longest number of ANY type for the country — for
 * India that is 13 digits (some toll-free / landline formats), so "1234567890000" could still be
 * typed into a mobile box. An Indian number is 10 digits; the field shows the national form, which
 * may carry the trunk "0" (11). Other countries keep the library's limit.
 */
const PhoneCountryCtx = createContext<string | undefined>('IN');
const nationalDigitLimit = (country: string | undefined, typed: string): number | undefined =>
  country === 'IN' ? (typed.replace(/\D/g, '').startsWith('0') ? 11 : 10) : undefined;

/** Keep only as much of `raw` as holds `limit` digits (formatting characters are kept as typed). */
const clipDigits = (raw: string, limit: number): string => {
  let n = 0; let out = '';
  for (const ch of raw) {
    if (/\d/.test(ch)) { if (n === limit) break; n++; }
    out += ch;
  }
  return out;
};

/** The number field itself: clips a keystroke or paste that would exceed the country's limit. */
const LimitedNumberInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function LimitedNumberInput({ onChange, ...rest }, ref) {
    const country = useContext(PhoneCountryCtx);
    return (
      <input
        ref={ref}
        {...rest}
        onChange={(e) => {
          const limit = nationalDigitLimit(country, e.target.value);
          if (limit !== undefined) {
            const clipped = clipDigits(e.target.value, limit);
            if (clipped !== e.target.value) e.target.value = clipped;
          }
          onChange?.(e);
        }}
      />
    );
  },
);

const callingCode = (country?: string): string => {
  if (!country) return '';
  try { return getCountryCallingCode(country as any); } catch { return ''; }
};

interface CountrySelectProps {
  value?: string;
  onChange: (v?: string) => void;
  options: { value?: string; label: string }[];
  disabled?: boolean;
}

/**
 * Custom country picker (Tailwind) replacing react-phone-number-input's native <select>: a flag +
 * dial-code trigger and a searchable dropdown (by country name or +code). Theme-aware via the app's
 * design tokens; the number field styling continues below via the injected CSS.
 */
function CountrySelect({ value, onChange, options, disabled }: CountrySelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setQuery(''); } };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const countries = options.filter((o) => o.value);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? countries.filter((o) =>
        o.label.toLowerCase().includes(q) ||
        `+${callingCode(o.value)}`.includes(q.replace(/\s/g, '')) ||
        o.value!.toLowerCase() === q)
    : countries;

  const SelFlag = value ? (flags as any)[value] : null;

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 items-center gap-1.5 rounded-l-md border border-line bg-surface px-2.5 text-sm text-content transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {SelFlag ? <SelFlag className="h-3.5 w-5 rounded-xs object-cover" /> : <Globe className="h-4 w-4 text-subtle" />}
        <span className="tabular-nums text-subtle">+{callingCode(value)}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-subtle transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+4px)] z-1000 w-72 overflow-hidden rounded-lg border border-line bg-surface shadow-lg">
          <div className="flex items-center gap-2 border-b border-line px-2.5 py-2">
            <Search className="h-4 w-4 shrink-0 text-subtle" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code…"
              aria-label="Search country or code"
              className="w-full bg-transparent text-sm text-content placeholder:text-subtle focus:outline-none"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-subtle">No match</li>
            ) : filtered.map((o) => {
              const F = (flags as any)[o.value!];
              const active = o.value === value;
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    onClick={() => { onChange(o.value); setOpen(false); setQuery(''); }}
                    className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm hover:bg-surface-2 ${active ? 'bg-brand-500/10' : ''}`}
                  >
                    {F ? <F className="h-3.5 w-5 shrink-0 rounded-xs object-cover" /> : <span className="h-3.5 w-5 shrink-0" />}
                    <span className="min-w-0 flex-1 truncate text-content">{o.label}</span>
                    <span className="shrink-0 tabular-nums text-xs text-subtle">+{callingCode(o.value)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Country-aware phone input: a searchable flag/country dropdown (defaulted to India) + a number field,
 * producing a strict E.164 string (e.g. +919812345678, +14155552671). Indian users see India
 * pre-selected and just type their 10 digits; anyone international searches their country. Drop-in for
 * an AntD <Form.Item> (it injects value/onChange). Backed by libphonenumber-js.
 */
export function PhoneInput({ value, onChange, placeholder, disabled, 'aria-label': ariaLabel }: Props) {
  // The selected country decides the typing limit (see LimitedNumberInput); starts from the stored number.
  const [country, setCountry] = useState<string | undefined>(
    () => parsePhoneNumberFromString(toE164(value) ?? '')?.country ?? 'IN',
  );
  return (
    <PhoneCountryCtx.Provider value={country}>
      <style>{`
        .gb-phone { display: flex; align-items: stretch; }
        .gb-phone .PhoneInputCountry { margin: 0; }
        /* The custom CountrySelect renders its own flag + arrow — hide RPN's defaults. */
        .gb-phone .PhoneInputCountryIcon, .gb-phone .PhoneInputCountrySelectArrow { display: none; }
        .gb-phone .PhoneInputInput {
          height: 36px; padding: 0 11px; flex: 1; min-width: 0;
          border: 1px solid var(--gb-border); border-left: none;
          border-radius: 0 6px 6px 0;
          background: var(--gb-surface); color: var(--gb-text);
          font-size: 14px; outline: none; transition: border-color .15s, box-shadow .15s;
        }
        .gb-phone .PhoneInputInput::placeholder { color: var(--gb-text-subtle); }
        .gb-phone .PhoneInputInput:focus { border-color: var(--color-brand-500); box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-brand-500) 22%, transparent); }
        .gb-phone .PhoneInputInput:disabled { background: var(--gb-surface-2); color: var(--gb-text-subtle); cursor: not-allowed; }
      `}</style>
      <RPNInput
        className="gb-phone"
        flags={flags}
        defaultCountry="IN"
        // MUST be an explicit `false` (not omitted): with the prop absent this version renders the
        // calling code INSIDE the number field, so the selector's "+91" plus the input's "+91" showed
        // the country code twice. `international={false}` = national number in the field, code only in
        // the selector.
        international={false}
        // Stop typing at the longest valid number: 10 digits for India (LimitedNumberInput — the
        // library's own limit allows 13 there), the library's per-country maximum elsewhere. Without
        // it the field took any number of digits — "1234567890000000".
        limitMaxLength
        inputComponent={LimitedNumberInput as any}
        onCountryChange={(c) => setCountry(c as string | undefined)}
        countrySelectComponent={CountrySelect as any}
        // Coerce legacy non-E.164 values (bare 10-digit / missing "+") so the number field shows a
        // clean national number — never the calling code twice.
        value={toE164(value)}
        onChange={(v) => onChange?.((v as string) || undefined)}
        placeholder={placeholder ?? 'Phone number'}
        aria-label={ariaLabel ?? placeholder ?? 'Phone number'}
        disabled={disabled}
      />
    </PhoneCountryCtx.Provider>
  );
}
